/**
 * Splat clip plane for reflection cameras (mirrors, water), on WebGPU.
 *
 * Reflection cameras clip splats on the wrong side of the reflecting plane through the
 * `uMirrorClipPlane` uniform (the gsplatModifyVS chunk the mirrors install). Each camera sets it in
 * its calculateProjection, which is in time for splats projected while drawing (WebGL's CPU-sort
 * renderer). The GPU-sort renderer (WebGPU) projects every camera's splats in a compute pass during
 * the gsplat director's update, before any camera's projection runs, so a reflection camera's
 * splats were projected unclipped: splats behind a mirror showed in it, and a pool floor in its
 * own water reflection.
 *
 * patchGpuSortClip wraps the GPU-sort renderer's per-camera projection to bind that camera's plane
 * around it. The plane is read from the camera entity (setSplatClipPlane), so callers keep the
 * array up to date before rendering and the projection sees the current one.
 */
import type * as pc from 'playcanvas';

/** Property on a reflection camera entity: the splat clip plane its GPU-sort projection uses. */
const CLIP_PLANE_KEY = '__storysplatSplatClipPlane';
const PATCHED_KEY = '__storysplatClipPatched';
const CLIP_OFF = new Float32Array([0, 0, 0, 0]);

interface GpuSortRenderer {
    usesGpuSort?: boolean;
    prepareRenderView?: (world: unknown, worldState: unknown, params: { cameraNode?: pc.Entity }) => unknown;
    device?: pc.GraphicsDevice;
}

/**
 * The plane (normal xyz, d) the GPU-sort projection clips this camera's splats with. Keep the same
 * array and update it in place when the plane moves; pass null to stop clipping for the camera.
 */
export function setSplatClipPlane(cameraEntity: pc.Entity, plane: Float32Array | null): void {
    const record = cameraEntity as unknown as Record<string, unknown>;
    if (plane) record[CLIP_PLANE_KEY] = plane;
    else delete record[CLIP_PLANE_KEY];
}

/** The plane set for a camera entity, if any. */
export function getSplatClipPlane(cameraEntity: unknown): Float32Array | undefined {
    return (cameraEntity as Record<string, unknown> | undefined)?.[CLIP_PLANE_KEY] as Float32Array | undefined;
}

/**
 * Wrap the GPU-sort renderer's per-camera projection (prepareRenderView) to bind the camera's
 * splat clip plane around it. Patched once per engine (on the prototype) and a no-op for cameras
 * without a plane. Returns false until a GPU-sort renderer exists (call again on a later frame);
 * WebGL's CPU-sort renderer never needs it, so a WebGL app keeps returning false harmlessly.
 */
export function patchGpuSortClip(app: pc.AppBase): boolean {
    const director = (app as unknown as { renderer?: { gsplatDirector?: { camerasMap?: Map<unknown, { layersMap: Map<unknown, { gsplatManager?: { renderer?: GpuSortRenderer } }> }> } } }).renderer?.gsplatDirector;
    if (!director?.camerasMap) return false;
    for (const cameraData of director.camerasMap.values()) {
        for (const layerData of cameraData.layersMap.values()) {
            const renderer = layerData.gsplatManager?.renderer;
            if (!renderer?.usesGpuSort || typeof renderer.prepareRenderView !== 'function') continue;
            const proto = Object.getPrototypeOf(renderer) as GpuSortRenderer & Record<string, unknown>;
            if (proto[PATCHED_KEY]) return true;
            const original = proto.prepareRenderView!;
            proto.prepareRenderView = function (this: GpuSortRenderer, world, worldState, params) {
                const plane = getSplatClipPlane(params?.cameraNode);
                const scope = plane ? this.device?.scope.resolve('uMirrorClipPlane') : undefined;
                if (!scope) return original.call(this, world, worldState, params);
                scope.setValue(plane);
                try {
                    return original.call(this, world, worldState, params);
                } finally {
                    scope.setValue(CLIP_OFF);
                }
            };
            proto[PATCHED_KEY] = true;
            return true;
        }
    }
    return false;
}
